import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bouffi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const boutiqueId = localStorage.getItem('boufi_boutique_id');
  if (boutiqueId) {
    config.headers['X-Boutique-Id'] = boutiqueId;
  }
  // Chamas multi-succursale : succursale active propagée sur tous les appels /chamas
  if (config.url && config.url.startsWith('/chamas')) {
    const chamasStore = localStorage.getItem('chamas_store_id');
    if (chamasStore && !(config.params && config.params.store_id)) {
      config.params = { ...config.params, store_id: chamasStore };
    }
  }
  // Anti-cache: forcer les données fraîches sur tous les GET
  if (config.method === 'get') {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('bouffi_token');
      localStorage.removeItem('bouffi_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Boutiques
export const boutiquesAPI = {
  getAll: () => api.get('/boutiques'),
  getOne: (id) => api.get(`/boutiques/${id}`),
  create: (data) => api.post('/boutiques', data),
  update: (id, data) => api.put(`/boutiques/${id}`, data),
};

// Inspections
export const inspectionsAPI = {
  getAll: () => api.get('/inspections'),
  getById: (id) => api.get(`/inspections/${id}`),
  getOne: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  delete: (id) => api.delete(`/inspections/${id}`),
  getContext: (boutiqueId) => api.get(`/inspections/context/${boutiqueId}`),
  getStats: (boutiqueId) => api.get('/inspections/stats/overview', { params: { boutique_id: boutiqueId } }),
};

// Innovations
export const innovationsAPI = {
  getAll: () => api.get('/innovations'),
  create: (data) => api.post('/innovations', data),
  vote: (id) => api.post(`/innovations/${id}/vote`),
};

// Formations
export const formationsAPI = {
  getAll: () => api.get('/formations'),
  create: (data) => api.post('/formations', data),
  delete: (id) => api.delete(`/formations/${id}`),
  seed: () => api.post('/formations/seed'),
  seedBoufiStandards: () => api.post('/formations/seed-boufi-standards'),
  aiStructure: (procedure_brute) => api.post('/formations/ai-structure', { procedure_brute }),
};

// Alertes
export const alertesAPI = {
  getAll: () => api.get('/alertes'),
  resolve: (id) => api.put(`/alertes/${id}/resolve`),
};

// Rentabilité
export const rentabiliteAPI = {
  getAll: (boutiqueId) => api.get('/rentabilite', { params: { boutique_id: boutiqueId } }),
  getOne: (id) => api.get(`/rentabilite/${id}`),
  create: (data) => api.post('/rentabilite', data),
  delete: (id) => api.delete(`/rentabilite/${id}`),
  exportPDF: (id) => api.get(`/rentabilite/export-pdf/${id}`),
  getComparatif: (periode1, periode2, boutiqueId) => 
    api.get('/rentabilite/comparatif-mercuriales', { params: { periode1, periode2, boutique_id: boutiqueId } }),
};

// Mercuriale (Gestion des coûts fournisseurs)
export const mercurialeAPI = {
  // Fournisseurs
  getSuppliers: () => api.get('/mercuriale/suppliers'),
  getSupplier: (id) => api.get(`/mercuriale/suppliers/${id}`),
  createSupplier: (data) => api.post('/mercuriale/suppliers', data),
  updateSupplier: (id, data) => api.put(`/mercuriale/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/mercuriale/suppliers/${id}`),
  
  // Produits fournisseur
  getProducts: (supplierId = null, category = null) => 
    api.get('/mercuriale/products', { params: { supplier_id: supplierId, category } }),
  getProduct: (id) => api.get(`/mercuriale/products/${id}`),
  createProduct: (data) => api.post('/mercuriale/products', data),
  updateProduct: (id, data) => api.put(`/mercuriale/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/mercuriale/products/${id}`),
  createBulkProducts: (products) => api.post('/mercuriale/products/bulk', products),
  
  // Résumé
  getSummary: () => api.get('/mercuriale/summary'),
  
  // OCR Import automatique
  extractFromImages: (images) => api.post('/mercuriale/ocr/extract', { images }),
  importFromOCR: (extractedData, supplierId = null) => 
    api.post('/mercuriale/ocr/import', extractedData, { params: { supplier_id: supplierId } }),
  
  // Frais de livraison
  getDeliveryFees: (supplierId = null, month = null) => 
    api.get('/mercuriale/delivery-fees', { params: { supplier_id: supplierId, month } }),
  getDeliveryFeesSummary: (month = null) => 
    api.get('/mercuriale/delivery-fees/summary', { params: { month } }),
  createDeliveryFee: (data) => api.post('/mercuriale/delivery-fees', data),
  deleteDeliveryFee: (id) => api.delete(`/mercuriale/delivery-fees/${id}`),
};

// Dashboard Rentabilité Avancé
export const rentabiliteDashboardAPI = {
  getDashboard: (boutiqueId, startDate = null, endDate = null) => 
    api.get(`/rentabilite/dashboard/${boutiqueId}`, { params: { start_date: startDate, end_date: endDate } }),
  simulate: (baseData, changes) => 
    api.post('/rentabilite/simulate', { base_data: baseData, changes }),
};

// Dashboard Global Unifié (Ventes + Coûts + Rentabilité)
export const globalDashboardAPI = {
  get: (boutiqueId = null, month = null) => 
    api.get('/dashboard/global', { params: { boutique_id: boutiqueId, month } }),
};

// Charges Fixes par Boutique
export const chargesFixesAPI = {
  get: (boutiqueId) => api.get(`/boutiques/${boutiqueId}/charges`),
  create: (boutiqueId, data) => api.post(`/boutiques/${boutiqueId}/charges`, data),
  update: (boutiqueId, chargeId, data) => api.put(`/boutiques/${boutiqueId}/charges/${chargeId}`, data),
  delete: (boutiqueId, chargeId) => api.delete(`/boutiques/${boutiqueId}/charges/${chargeId}`),
};

// Masse Salariale (Payroll)
export const payrollAPI = {
  getAll: (boutiqueId = null, month = null) => 
    api.get('/payroll', { params: { boutique_id: boutiqueId, month } }),
  getSummary: (boutiqueId = null, month = null) => 
    api.get('/payroll/summary', { params: { boutique_id: boutiqueId, month } }),
  getEstimate: (boutiqueId = null, month = null) =>
    api.get('/payroll/estimate', { params: { boutique_id: boutiqueId, month } }),
  getReference: (boutiqueId) =>
    api.get('/payroll/reference', { params: { boutique_id: boutiqueId } }),
  saveReference: (data) => api.post('/payroll/reference', data),
  simulate: (data) => api.post('/payroll/simulate', data),
  fromPlanning: (boutiqueId) =>
    api.get('/payroll/from-planning', { params: { boutique_id: boutiqueId } }),
  create: (data) => api.post('/payroll', data),
  extractFromImage: (images, boutiqueId) => 
    api.post('/payroll/extract-from-image', { images, boutique_id: boutiqueId }),
  delete: (payslipId) => api.delete(`/payroll/${payslipId}`),
};

// Recettes / Compositions de produits
export const recipesAPI = {
  getAll: (category = null) => api.get('/recipes', { params: { category } }),
  getOne: (id) => api.get(`/recipes/${id}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
  importFoodCost: (images) => api.post('/recipes/import-foodcost', { images }),
};

// Menu Import (depuis site web)
export const menuAPI = {
  getScrapedProducts: () => api.get('/menu/scraped-products'),
  importFromWebsite: () => api.post('/menu/import-from-website'),
  clearImported: () => api.delete('/menu/clear-imported'),
};

// Excel Food Cost Import
export const excelFoodCostAPI = {
  importLatte: () => api.post('/recipes/import-excel-foodcost'),
  uploadExcel: (formData) => api.post('/recipes/import-excel-foodcost', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Food Cost Dashboard
export const foodCostAPI = {
  getDashboard: () => api.get('/dashboard/food-cost'),
  importExcel: (formData) => api.post('/recipes/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getImportPreview: () => api.get('/recipes/import-excel/preview'),
  exportCSV: () => api.get('/food-cost/export/csv', { responseType: 'blob' }),
  exportExcel: () => api.get('/food-cost/export/excel', { responseType: 'blob' }),
  exportPDF: () => api.get('/food-cost/export/pdf', { responseType: 'blob' }),
};

// Uber Eats Intelligence
export const ubereatsAPI = {
  uploadCSV: (formData) => api.post('/ubereats/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadProducts: (formData) => api.post('/ubereats/upload-products', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadHourly: (formData) => api.post('/ubereats/upload-hourly', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDashboard: () => api.get('/ubereats/dashboard'),
  getHourlyData: () => api.get('/ubereats/hourly'),
  getCookies: () => api.get('/ubereats/cookies'),
  getAIRecommendations: () => api.get('/ubereats/ai-recommendations'),
};

// Multi-Canal
export const multicanalAPI = {
  getDashboard: () => api.get('/dashboard/multicanal'),
  downloadReport: (params) => api.get('/reports/multicanal', {
    params,
    responseType: 'blob',
  }),
};

// Stock Management
export const stockAPI = {
  getDashboard: () => api.get('/stock/dashboard'),
  getZones: () => api.get('/stock/zones'),
  getZone: (zoneId) => api.get(`/stock/zones/${zoneId}`),
  affecterProduit: (zoneId, szId, produitId) => api.put(`/stock/zones/${zoneId}/sous-zones/${szId}/affecter`, { produit_id: produitId }),
  getProduits: () => api.get('/stock/produits'),
  createProduit: (data) => api.post('/stock/produits', data),
  updateProduit: (id, data) => api.put(`/stock/produits/${id}`, data),
  getFournisseurs: () => api.get('/stock/fournisseurs'),
  getMouvements: (params) => api.get('/stock/mouvements', { params }),
  createMouvement: (data) => api.post('/stock/mouvements', data),
  entreeRapide: (data) => api.post('/stock/entree-rapide', data),
  seed: () => api.post('/stock/seed'),
  reseed: () => api.post('/stock/reseed'),
  getFrigo: () => api.get('/stock/frigo'),
  getParfums: () => api.get('/stock/parfums'),
  createPorte: (data) => api.post('/stock/portes', data),
  updatePorte: (porteNum, data) => api.put(`/stock/portes/${porteNum}`, data),
  deletePorte: (porteNum) => api.delete(`/stock/portes/${porteNum}`),
  ajuster: (data) => api.post('/stock/ajustement', data),
  getSorties: () => api.get('/stock/sorties'),
  getOverview: () => api.get('/stock/overview'),
  getAlertes: () => api.get('/stock/alertes'),
  // Tampon
  getTampon: () => api.get('/stock/tampon'),
  ajusterTampon: (data) => api.put('/stock/tampon/ajuster', data),
  transfertTampon: (data) => api.post('/stock/tampon/transfert', data),
  getHistoriqueTransferts: () => api.get('/stock/tampon/historique'),
  annulerTransfert: (id) => api.post(`/stock/tampon/annuler/${id}`),
  interTransfert: (data) => api.post('/stock/tampon/inter-transfert', data),
  ajouterTampon: (data) => api.post('/stock/tampon/ajouter', data),
  // Pertes & Offerts
  getPertesToday: () => api.get('/stock/pertes/today'),
  savePertes: (data) => api.post('/stock/pertes', data),
  getPertesHistorique: (days = 30) => api.get(`/stock/pertes/historique?days=${days}`),
  getPertesRecap: (mois) => api.get(`/stock/pertes/recap-mensuel${mois ? '?mois=' + mois : ''}`),
  // Zones CRUD
  createZone: (data) => api.post('/stock/zones', data),
  updateZone: (id, data) => api.put(`/stock/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/stock/zones/${id}`),
  updateZoneProduits: (id, produits) => api.put(`/stock/zones/${id}/produits`, { produits_stockes: produits }),
  // Sous-zones CRUD
  createSousZone: (zoneId, data) => api.post(`/stock/zones/${zoneId}/sous-zones`, data),
  updateSousZone: (zoneId, szId, data) => api.put(`/stock/zones/${zoneId}/sous-zones/${szId}`, data),
  deleteSousZone: (zoneId, szId) => api.delete(`/stock/zones/${zoneId}/sous-zones/${szId}`),
  // Phase 2
  getMappings: () => api.get('/stock/mapping'),
  createMapping: (data) => api.post('/stock/mapping', data),
  seedMappings: () => api.post('/stock/mapping/seed'),
  fixMappings: () => api.post('/stock/mapping/fix'),
  debugDeduction: (data) => api.post('/stock/debug-deduction', data),
  enregistrerVente: (data) => api.post('/stock/vente', data),
  enregistrerPerte: (data) => api.post('/stock/pertes', data),
  getPertes: (days) => api.get('/stock/pertes', { params: { days } }),
  getPertesDashboard: (days) => api.get('/stock/pertes/dashboard', { params: { days } }),
  saisirInventaire: (data) => api.post('/stock/inventaire', data),
  corrigerInventaire: (id) => api.post(`/stock/inventaire/${id}/corriger`),
  getInventaires: () => api.get('/stock/inventaires'),
  inventaireComplet: (data) => api.post('/stock/inventaire/complet', data),
  getInventairesComplets: (lieu) => api.get('/stock/inventaires/complets', { params: lieu ? { lieu } : {} }),
  getStockActuelPourInventaire: (lieu) => api.get('/stock/inventaire/stock-actuel', { params: { lieu } }),
  getAlertes: (statut) => api.get('/stock/alertes', { params: { statut } }),
  ackAlerte: (id) => api.put(`/stock/alertes/${id}/ack`),
  checkAlertes: () => api.post('/stock/alertes/check'),
  // Phase 3 IA
  analyzeWaste: () => api.post('/stock/ia/analyze-waste'),
  predictRestock: () => api.post('/stock/ia/predict-restock'),
  generateMessage: (fournisseur, data) => api.post(`/stock/ia/generate-message/${fournisseur}`, data),
  dailyCheck: () => api.post('/stock/ia/daily-check'),
  getIaHistory: (type) => api.get('/stock/ia/history', { params: { type_filter: type } }),
  getIaMessages: (statut) => api.get('/stock/ia/messages', { params: { statut } }),
  // Notifications
  getNotifications: (lu) => api.get('/stock/notifications', { params: { lu } }),
  markNotificationRead: (id) => api.put(`/stock/notifications/${id}/read`),
  approveNotification: (id) => api.put(`/stock/notifications/${id}/approve`),
  dismissNotification: (id) => api.put(`/stock/notifications/${id}/dismiss`),
  clearReadNotifications: () => api.delete('/stock/notifications/read'),
  // Sync stock
  syncBiborne: (date) => api.post('/stock/sync-biborne', { date }),
};


// Boufi Score & Intelligence
export const boufiScoreAPI = {
  getScore: (boutiqueId, month) => api.get('/boufi-score', { params: { boutique_id: boutiqueId, month } }),
};

export const aiAPI = {
  getRecommendations: (boutiqueId, month) => api.post('/ai/recommendations', null, { params: { boutique_id: boutiqueId, month } }),
};

// COO Boufi (COO Virtuel)
export const cooAPI = {
  getBrief: (boutiqueId, month) => api.get(`/coo/brief/${boutiqueId}`, { params: { month } }),
};

export const simulationAPI = {
  whatIf: (params, boutiqueId, month) => api.post('/simulation/what-if', params, { params: { boutique_id: boutiqueId, month } }),
};

// OCR
export const ocrAPI = {
  extractFacture: (filename) => api.post('/ocr/extract-facture', { filename }),
};

// Dashboard stats
export const statsAPI = {
  getDashboard: () => api.get('/stats/dashboard'),
  getRentabiliteEvolution: (months = 6, boutiqueId = null) => 
    api.get('/stats/rentabilite-evolution', { params: { months, boutique_id: boutiqueId } }),
};

// Upload
export const uploadAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Predictions Tracker
export const predictionsAPI = {
  generatePrediction: (boutiqueId, date) => api.post(`/predictions/generate/${boutiqueId}`, null, { params: { date } }),
  getToday: (boutiqueId) => api.get(`/predictions/today/${boutiqueId}`),
  compare: (boutiqueId, date) => api.get(`/predictions/compare/${boutiqueId}/${date}`),
  getHistory: (boutiqueId, days = 14) => api.get(`/predictions/history/${boutiqueId}`, { params: { days } }),
  calibrate: (boutiqueId) => api.post(`/predictions/calibrate/${boutiqueId}`),
  getAutoLearnStatus: (boutiqueId) => api.get(`/predictions/auto-learn/status/${boutiqueId}`),
  triggerAutoLearn: (boutiqueId) => api.post(`/predictions/auto-learn/trigger/${boutiqueId}`),
  getLiveCa: (boutiqueId) => api.get(`/predictions/live-ca/${boutiqueId}`),
  getAccuracyTrend: (boutiqueId, weeks = 8) => api.get(`/predictions/accuracy-trend/${boutiqueId}`, { params: { weeks } }),
  getOpeningHours: (boutiqueId) => api.get(`/predictions/opening-hours/${boutiqueId}`),
  updateOpeningHours: (boutiqueId, hours) => api.put(`/predictions/opening-hours/${boutiqueId}`, { hours }),
  getSchedulerStatus: () => api.get('/predictions/scheduler/status'),
  triggerJob: (jobName) => api.post(`/predictions/scheduler/run/${jobName}`),
};

// Seed data
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Knowledge Base
export const knowledgeAPI = {
  getDocuments: (params) => api.get('/knowledge/documents', { params }),
  getDocument: (id) => api.get(`/knowledge/documents/${id}`),
  createDocument: (data) => api.post('/knowledge/documents', data),
  updateDocument: (id, data) => api.put(`/knowledge/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/knowledge/documents/${id}`),
  uploadFile: (formData) => api.post('/knowledge/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFile: (fileId) => api.get(`/knowledge/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (fileId) => api.delete(`/knowledge/files/${fileId}`),
  getCategories: () => api.get('/knowledge/categories'),
  getTags: () => api.get('/knowledge/tags'),
  getStats: () => api.get('/knowledge/stats'),
};

export const cookieCountAPI = {
  getToday: (boutiqueId) => api.get(`/cookie-count/today/${boutiqueId}`),
  getByDate: (boutiqueId, date) => api.get(`/cookie-count/date/${boutiqueId}/${date}`),
  getRange: (boutiqueId, start, end) => api.get(`/cookie-count/range/${boutiqueId}`, { params: { start, end } }),
  getHistory: (boutiqueId, days = 7) => api.get(`/cookie-count/history/${boutiqueId}`, { params: { days } }),
  getSummary: (boutiqueId) => api.get(`/cookie-count/summary/${boutiqueId}`),
  getCroisement: (boutiqueId, date) => api.get(`/cookie-count/croisement/${boutiqueId}`, { params: date ? { date } : {} }),
  getCroisementAuto: (boutiqueId) => api.get(`/cookie-count/croisement-auto/${boutiqueId}`),
  forceSync: () => api.post('/biborne/sync-now'),
};

export const drinkCountAPI = {
  getToday: (boutiqueId) => api.get(`/item-count/drinks/today/${boutiqueId}`),
  getByDate: (boutiqueId, date) => api.get(`/item-count/drinks/date/${boutiqueId}/${date}`),
  getRange: (boutiqueId, start, end) => api.get(`/item-count/drinks/range/${boutiqueId}`, { params: { start, end } }),
};

export const iceCreamCountAPI = {
  getToday: (boutiqueId) => api.get(`/item-count/ice-cream/today/${boutiqueId}`),
  getByDate: (boutiqueId, date) => api.get(`/item-count/ice-cream/date/${boutiqueId}/${date}`),
  getRange: (boutiqueId, start, end) => api.get(`/item-count/ice-cream/range/${boutiqueId}`, { params: { start, end } }),
};

export const productionAPI = {
  getPlan: (boutiqueId) => api.get(`/production/plan/${boutiqueId}`),
  getTypicalPlan: () => api.get('/production/plan'),
  getConsumption: (boutiqueId) => api.get(`/production/consumption/${boutiqueId}`),
  getOven: (boutiqueId) => api.get(`/production/oven/${boutiqueId}`),
  startBatch: (boutiqueId, data) => api.post(`/production/start-batch/${boutiqueId}`, data),
  endCooking: (boutiqueId, batchId) => api.post(`/production/end-cooking/${boutiqueId}/${batchId}`),
  endCooling: (boutiqueId, batchId) => api.post(`/production/end-cooling/${boutiqueId}/${batchId}`),
  transferVitrine: (boutiqueId, batchId) => api.post(`/production/transfer-vitrine/${boutiqueId}/${batchId}`),
  cancelBatch: (boutiqueId, batchId) => api.post(`/production/cancel-batch/${boutiqueId}/${batchId}`),
  markOutage: (boutiqueId, data) => api.post(`/production/mark-outage/${boutiqueId}`, data),
  updateVitrine: (boutiqueId, data) => api.post(`/production/vitrine/${boutiqueId}`, data),
  getLog: (boutiqueId, period) => api.get(`/production/log/${boutiqueId}`, { params: period ? { period } : {} }),
  getRecap: (boutiqueId) => api.get(`/production/recap/${boutiqueId}`),
  clearLog: (boutiqueId) => api.delete(`/production/log/${boutiqueId}`),
};

// ==================== ALERTS ====================
export const alertsAPI = {
  getAlerts: () => api.get('/alerts'),
  getCount: () => api.get('/alerts/count'),
  markRead: (id) => api.post(`/alerts/${id}/read`),
  markAllRead: () => api.post('/alerts/read-all'),
  testGenerate: () => api.post('/alerts/test-generate'),
};

export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  getNotifications: () => api.get('/notifications'),
  dismissNotification: (id) => api.post(`/notifications/${id}/dismiss`),
  dismissAll: () => api.post('/notifications/dismiss-all'),
};

// ==================== ORDERING PROGRAM ====================
export const orderingAPI = {
  getConfig: () => api.get('/stock/ordering/config'),
  updateConfig: (data) => api.put('/stock/ordering/config', data),
  preview: () => api.get('/stock/ordering/preview'),
  send: () => api.post('/stock/ordering/send'),
  history: () => api.get('/stock/ordering/history'),
};

// ==================== LEAGUE ====================
export const leagueAPI = {
  getProfile: () => api.get('/league/profile'),
  getBadges: () => api.get('/league/badges'),
  getStreak: () => api.get('/league/streak'),
  useShield: () => api.post('/league/streak/shield'),
  checkBadges: () => api.post('/league/badges/check'),
  getChallenges: () => api.get('/league/challenges'),
  getLeaderboard: (period = 'weekly') => api.get(`/league/leaderboard?period=${period}`),
  getCoaching: () => api.get('/league/coaching'),
  getPointeuseScore: () => api.get('/league/pointeuse-score'),
};

// ==================== LOYALTY / FIDELITE ====================
export const loyaltyAPI = {
  getStats: () => api.get('/loyalty/stats'),
  getMenu: () => api.get('/loyalty/menu'),
  searchMember: (phone) => api.get(`/loyalty/members/search?phone=${phone}`),
  getMember: (id) => api.get(`/loyalty/members/${id}`),
  createMember: (data) => api.post('/loyalty/members', data),
  addStamp: (id, data) => api.post(`/loyalty/members/${id}/stamp`, data || {}),
  claimReward: (id) => api.post(`/loyalty/members/${id}/reward`),
  correctStamps: (id, data) => api.put(`/loyalty/members/${id}/stamps`, data),
  claimBirthday: (id) => api.post(`/loyalty/members/${id}/birthday`),
  listMembers: () => api.get('/loyalty/members'),
  pushBroadcast: (data) => api.post('/loyalty/push-broadcast', data),
  pushSubscribersCount: () => api.get('/loyalty/push-subscribers-count'),
  // Promo banners
  createPromoBanner: (formData) => api.post('/loyalty/promo-banner', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listPromoBanners: () => api.get('/loyalty/promo-banners'),
  deletePromoBanner: (id) => api.delete(`/loyalty/promo-banner/${id}`),
  togglePromoBanner: (id) => api.put(`/loyalty/promo-banner/${id}/toggle`),
  // Admin / Direction
  adminDashboard: (boutiqueId) => api.get(`/loyalty/admin/dashboard${boutiqueId ? `?boutique_id=${boutiqueId}` : ''}`),
  adminMembers: () => api.get('/loyalty/admin/members'),
  adminMemberDetail: (id) => api.get(`/loyalty/admin/members/${id}`),
  adminUpdateMember: (id, data) => api.put(`/loyalty/admin/members/${id}`, data),
  adminDeleteMember: (id) => api.delete(`/loyalty/admin/members/${id}`),
  // Boutique codes
  getBoutiqueCodes: () => api.get('/loyalty/admin/boutique-codes'),
  updateBoutiqueCodes: (codes) => api.put('/loyalty/admin/boutique-codes', { codes }),
  initBoutiqueCodes: () => api.post('/loyalty/admin/init-boutique-codes'),
  // Stats par boutique
  statsByBoutique: () => api.get('/loyalty/admin/stats-by-boutique'),
  // Export CSV
  exportMembers: (boutiqueCode) => api.get('/loyalty/admin/export', { params: { boutique_code: boutiqueCode || '' }, responseType: 'blob' }),
  adminCatalog: () => api.get('/loyalty/admin/catalog'),
  adminGetConfig: (boutiqueId) => api.get(`/loyalty/admin/config/${boutiqueId}`),
  adminUpdateConfig: (boutiqueId, data) => api.put(`/loyalty/admin/config/${boutiqueId}`, data),
  // Campaigns QR
  createCampaign: (data) => api.post('/loyalty/campaigns', data),
  listCampaigns: () => api.get('/loyalty/campaigns'),
  deleteCampaign: (id) => api.delete(`/loyalty/campaigns/${id}`),
  toggleCampaign: (id) => api.put(`/loyalty/campaigns/${id}/toggle`),
  downloadQR: (id) => api.get(`/loyalty/campaigns/${id}/qr`, { responseType: 'blob' }),
  // Challenge Analytics
  challengeStats: () => api.get('/loyalty/challenges/stats'),
  // Goals
  getGoals: () => api.get('/loyalty/goals'),
  updateGoals: (data) => api.put('/loyalty/goals', data),
  // Admin Notifications
  adminNotifications: () => api.get('/loyalty/admin/notifications'),
  markNotifRead: (id) => api.put(`/loyalty/admin/notifications/${id}/read`),
  markAllNotifsRead: () => api.put('/loyalty/admin/notifications/read-all'),
  // Admin Rewards
  adminRewards: (status) => api.get('/loyalty/admin/rewards', { params: { status } }),
  adminValidateReward: (spinId) => api.put(`/loyalty/admin/rewards/${spinId}/validate`),
  adminValidateReview: (spinId) => api.post(`/loyalty/admin/challenges/${spinId}/validate-review`),
  adminRejectReview: (spinId) => api.post(`/loyalty/admin/challenges/${spinId}/reject-review`),
  adminMemberRewards: (memberId) => api.get(`/loyalty/admin/member/${memberId}/rewards`),
  caisseSearch: (query) => api.get('/loyalty/admin/caisse/search', { params: { query } }),
  // Analytics avancees
  analyticsOverview: () => api.get('/loyalty/analytics/overview'),
};


// ==================== DAILY PERFORMANCE ====================
export const eventsAPI = {
  list: (params = {}) => api.get('/events', { params }),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  remove: (id) => api.delete(`/events/${id}`),
  seedDefaults: () => api.post('/events/seed-defaults'),
};

export const dailyPerformanceAPI = {
  getBiborneLive: () => api.get('/biborne/live'),
  getLaborRatio: () => api.get('/planning/labor-ratio'),
  getStockCurrent: () => api.get('/stock-production/stock/current'),
  getWhosHere: () => api.get('/planning/whos-here'),
  getObjectivesDaily: () => api.get('/objectives/daily'),
  getLoyaltyStats: () => api.get('/loyalty/stats'),
  getComparison: () => api.get('/dashboard/performance-comparison'),
  getProductionStats: () => api.get('/batch-planning/production-stats'),
};

// Rapports hebdomadaires
export const weeklyReportAPI = {
  getThresholds: () => api.get('/reports/weekly/thresholds'),
  updateThresholds: (data) => api.put('/reports/weekly/thresholds', data),
  getData: (week) => api.get('/reports/weekly/data', { params: { week } }),
  exportPdf: (week) => api.get('/reports/weekly/export-pdf', { params: { week }, responseType: 'blob' }),
  history: () => api.get('/reports/weekly/history'),
  applySuggestion: (target_week, overwrite = false) =>
    api.post('/reports/weekly/apply-suggestion', { target_week, overwrite }),
  sendWhatsApp: (week, recipients) =>
    api.post('/reports/weekly/send-whatsapp', { week, recipients }),
};

export const campaignCostsAPI = {
  list: (campaignId) => api.get(`/marketing-events/${campaignId}/costs`),
  addInfluencer: (campaignId, data) => api.post(`/marketing-events/${campaignId}/influencers`, data),
  addOtherCost: (campaignId, data) => api.post(`/marketing-events/${campaignId}/costs`, data),
  uploadInvoice: (campaignId, formData) =>
    api.post(`/marketing-events/${campaignId}/invoices`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCost: (campaignId, costId) => api.delete(`/marketing-events/${campaignId}/costs/${costId}`),
  bulkAddCosts: (campaignId, payload) => api.post(`/marketing-events/${campaignId}/costs/bulk`, payload),
  downloadInvoice: (campaignId, invoiceId) => `${process.env.REACT_APP_BACKEND_URL}/api/marketing-events/${campaignId}/invoices/${invoiceId}/download`,
  monthly: (month) => api.get('/marketing-events/costs/monthly', { params: month ? { month } : {} }),
};

export default api;
